import Swal from 'sweetalert2';
import { useEffect, useState } from 'react';
// Componentes
import ModalConfirmarHorario from './modalConfirmarHorario';
import ModalCambiarHorario from './modalCambiarHorario';
import ModalCompletarReserva from './modalCompletarReserva';
import ModalDisputas from './modalDisputas';
import ModalValoracion from './modalValoracion';

// Cuenta regresiva del plazo para confirmar o cambiar el horario en reservas flexibles.
// El plazo lo fija el backend al pagar (confirmacionHorarioExpiraEn = compra + 1 h, ver wompi.js) y NO se
// reinicia al proponer otro horario. Al vencer, el cron modificarReservasFlexibles (cada 10 min) confirma
// automáticamente el último horario propuesto.
// Usa la hora del servidor (ahoraServidor) como referencia para no depender del reloj del navegador.
function CuentaRegresivaHorario({ expiraEn, ahoraServidor, horaPropuesta }) {
    const [desfaseMs] = useState(() => {
        const servidor = ahoraServidor ? new Date(ahoraServidor).getTime() : NaN;
        return Number.isFinite(servidor) ? servidor - Date.now() : 0;
    });
    const [ahora, setAhora] = useState(() => Date.now() + desfaseMs);

    useEffect(() => {
        const id = setInterval(() => setAhora(Date.now() + desfaseMs), 1000);
        return () => clearInterval(id);
    }, [desfaseMs]);

    const limite = expiraEn ? new Date(expiraEn).getTime() : NaN;
    if (!Number.isFinite(limite)) return null;

    const restanteMs = Math.max(0, limite - ahora);
    const vencido = restanteMs === 0;
    const horas = Math.floor(restanteMs / 3600000);
    const minutos = Math.floor((restanteMs % 3600000) / 60000);
    const segundos = Math.floor((restanteMs % 60000) / 1000);
    const dos = (n) => String(n).padStart(2, "0");
    const texto = horas > 0 ? `${horas}:${dos(minutos)}:${dos(segundos)}` : `${dos(minutos)}:${dos(segundos)}`;
    const urgente = !vencido && restanteMs <= 10 * 60 * 1000; // últimos 10 minutos
    const hora = horaPropuesta && horaPropuesta !== "NA" ? horaPropuesta : null;
    const horaLimite = new Date(limite).toLocaleTimeString("es-CO", { hour: "numeric", minute: "2-digit", timeZone: "America/Bogota" });

    return (
        <div
            className="rounded-4 mt-4 mb-0 p-3 d-flex align-items-center gap-3"
            style={{
                background: vencido ? "rgba(var(--verde-bootstrap-rgb), 0.08)" : urgente ? "rgba(var(--rojo-error-rgb), 0.08)" : "rgba(var(--azul-medio-rgb), 0.06)",
                border: `1px solid ${vencido ? "rgba(var(--verde-bootstrap-rgb), 0.25)" : urgente ? "rgba(var(--rojo-error-rgb), 0.25)" : "var(--color-border)"}`,
            }}
        >
            <i className={`bi ${vencido ? "bi-check2-circle" : "bi-stopwatch"}`} style={{ fontSize: "1.6rem", color: vencido ? "var(--verde-bootstrap)" : urgente ? "var(--rojo-error)" : "var(--azul-logo-claro)" }}></i>
            <div className="flex-grow-1">
                {vencido ? (
                    <>
                        <p className="fw-semibold mb-1">El tiempo para cambiar el horario terminó</p>
                        <p className="mb-0 small text-muted">
                            {hora ? `El horario de las ${hora} se confirmará automáticamente en unos minutos.` : "El último horario propuesto se confirmará automáticamente en unos minutos."}
                        </p>
                    </>
                ) : (
                    <>
                        <p className="mb-1 small text-muted">Tiempo restante para confirmar o cambiar el horario</p>
                        <p className="mb-1 fw-bold" style={{ fontSize: "1.6rem", lineHeight: 1.1, fontVariantNumeric: "tabular-nums", color: urgente ? "var(--rojo-error)" : "var(--color-text-primary)" }}>
                            {texto}
                        </p>
                        <p className="mb-0 small text-muted">
                            Hasta las {horaLimite}. Si nadie responde, se confirmará automáticamente {hora ? `el horario de las ${hora}` : "el último horario propuesto"}.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

function EstadoReserva({ reserva, quienEs, usuario, servicio, ahoraServidor, valoraciones, soloLectura = false }) {
    const estado = reserva?.estado || "hold";
    const tipoHold = reserva?.tipoHold || "NA";
    const reclamoAbierto = reserva?.reclamo?.abierto === true || ["abierto", "en_revision"].includes(reserva?.reclamo?.estado);
    const esAgendaFlexible = String(reserva?.modoAgenda || "").toLowerCase() === "flexible";
    const mostrarModalesHorario = esAgendaFlexible && estado === "hold" && tipoHold === "confirmacionHorario";
    const horarioPropuestoPor = reserva?.horarioPropuestoPor || "Comprador";
    const puedeResponderHorario = mostrarModalesHorario && horarioPropuestoPor !== quienEs;
    const textoEsperandoHorario = horarioPropuestoPor === "Comprador"
        ? "Esperando confirmación del vendedor"
        : "Esperando confirmación del comprador";
    const ahoraActual = ahoraServidor ? new Date(ahoraServidor) : new Date();
    const fechaFinReserva = reserva?.fin ? new Date(reserva.fin) : null;
    const fechaFin = fechaFinReserva instanceof Date && !Number.isNaN(fechaFinReserva.getTime())
        ? fechaFinReserva.toLocaleString("es-CO", {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: "America/Bogota",
        })
        : "NA";
    const puedeCompletarOReclamar = fechaFinReserva instanceof Date && !Number.isNaN(fechaFinReserva.getTime())
        ? ahoraActual >= fechaFinReserva
        : false;

    const estadoConfig = (() => {
        if (reclamoAbierto) {
            return {
                titulo: "Reserva en reclamo",
                descripcion: "El comprador reportó un problema con el servicio. Zervicy revisará la información de la reserva y el chat antes de liberar el pago al vendedor.",
                pasoActivo: 3,
                badge: "En reclamo",
                icono: "bi-exclamation-triangle",
                color: "var(--rojo-error)",
                fondo: "rgba(var(--rojo-error-rgb), 0.12)",
                mostrarBotonCompletar: false,
            };
        }
        if (estado === "hold" && tipoHold === "checkout") {
            return {
                titulo: "Estamos confirmando tu pago",
                descripcion: "Tu reserva está apartada mientras terminamos de validar el pago.",
                pasoActivo: 1,
                badge: "Pago en proceso",
                icono: "bi-credit-card",
                color: "var(--naranja-cta)",
                fondo: "rgba(var(--naranja-cta-rgb), 0.12)",
                mostrarBotonCompletar: false,
            };
        }

        if (estado === "hold" && tipoHold === "confirmacionHorario") {
            return {
                titulo: "Esperando confirmación de horario",
                descripcion: "Ya compraste el servicio. Estamos esperando que el vendedor confirme o ajuste la hora solicitada.",
                pasoActivo: 2,
                badge: "Pendiente horario",
                icono: "bi-clock-history",
                color: "var(--naranja-cta)",
                fondo: "rgba(var(--naranja-cta-rgb), 0.12)",
                mostrarBotonCompletar: false,
            };
        }

        if (estado === "confirmado") {
            return {
                titulo: "Tu servicio está confirmado",
                descripcion: "Todo está listo. Ahora solo espera la visita o la prestación del servicio en la fecha acordada.",
                pasoActivo: 3,
                badge: "Confirmado",
                icono: "bi-calendar-check",
                color: "var(--verde-bootstrap)",
                fondo: "rgba(var(--verde-bootstrap-rgb), 0.12)",
                mostrarBotonCompletar: true,
            };
        }

        if (estado === "completado") {
            return {
                titulo: "Servicio completado",
                descripcion: "Marcaste este servicio como completado. Gracias por usar Zervicy.",
                pasoActivo: 4,
                badge: "Completado",
                icono: "bi-check2-circle",
                color: "var(--verde-bootstrap)",
                fondo: "rgba(var(--verde-bootstrap-rgb), 0.12)",
                mostrarBotonCompletar: false,
            };
        }

        if (estado === "cancelado") {
            return {
                titulo: "Reserva cancelada",
                descripcion: "Esta reserva fue cancelada. Si necesitas ayuda, puedes revisar el detalle o contactar soporte.",
                pasoActivo: 0,
                badge: "Cancelada",
                icono: "bi-x-circle",
                color: "var(--rojo-error)",
                fondo: "rgba(var(--rojo-error-rgb), 0.12)",
                mostrarBotonCompletar: false,
            };
        }

        if (estado === "expirado") {
            return {
                titulo: "Reserva expirada",
                descripcion: "El tiempo para completar esta reserva terminó. Puedes volver a reservar el servicio si aún está disponible.",
                pasoActivo: 0,
                badge: "Expirada",
                icono: "bi-hourglass-split",
                color: "var(--color-text-secondary)",
                fondo: "rgba(var(--texto-gris-oscuro-rgb), 0.12)",
                mostrarBotonCompletar: false,
            };
        }

        return {
            titulo: "Estado de reserva",
            descripcion: "Estamos revisando el estado actual de tu reserva.",
            pasoActivo: 1,
            badge: "En revisión",
            icono: "bi-info-circle",
            color: "var(--color-text-secondary)",
            fondo: "rgba(var(--texto-gris-oscuro-rgb), 0.12)",
            mostrarBotonCompletar: false,
        };
    })();

    const pasos = reclamoAbierto
        ? [
            { numero: 1, titulo: "Compra", texto: "Tu reserva fue creada." },
            { numero: 2, titulo: "Horario", texto: "Confirmamos la fecha y hora." },
            { numero: 3, titulo: "Reclamo", texto: "El caso está en revisión y el pago está retenido." },
            { numero: 4, titulo: "Solución", texto: "Zervicy dará una respuesta al caso." },
        ]
        : [
            { numero: 1, titulo: "Compra", texto: "Tu reserva fue creada." },
            { numero: 2, titulo: "Horario", texto: "Confirmamos la fecha y hora." },
            { numero: 3, titulo: "Servicio", texto: "El vendedor prestará el servicio." },
            { numero: 4, titulo: "Finalizado", texto: "Marcas el servicio como completado." },
        ];


    const yaEnvioValoracion = Array.isArray(valoraciones)
        ? valoraciones.some((valoracion) => String(valoracion?.idComprador || "") === String(reserva?.idComprador || ""))
        : false;

    return (
        <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
                <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
                    <div>
                        <span
                            className="badge rounded-pill px-3 py-2 mb-3"
                            style={{ background: estadoConfig.fondo, color: estadoConfig.color }}
                        >
                            {estadoConfig.badge}
                        </span>
                        <h2 className="h5 fw-bold text-dark mb-2">{estadoConfig.titulo}</h2>
                        <p className="text-muted mb-0" style={{ fontSize: "0.95rem", lineHeight: 1.5 }}>
                            {estadoConfig.descripcion}
                        </p>
                    </div>

                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{ width: 48, height: 48, background: estadoConfig.fondo, color: estadoConfig.color }}
                    >
                        <i className={`bi ${estadoConfig.icono}`} style={{ fontSize: "1.45rem" }}></i>
                    </div>
                </div>

                <div className="d-flex flex-column gap-3 mb-0">
                    {pasos.map((paso, index) => {
                        const completado = estadoConfig.pasoActivo >= paso.numero;
                        const activo = estadoConfig.pasoActivo === paso.numero;

                        return (
                            <div key={paso.numero} className="d-flex gap-3 position-relative">
                                {index < pasos.length - 1 ? (
                                    <div
                                        className="position-absolute"
                                        style={{
                                            left: 17,
                                            top: 38,
                                            bottom: -18,
                                            width: 2,
                                            background: completado ? estadoConfig.color : "var(--color-border)",
                                        }}
                                    />
                                ) : null}

                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold"
                                    style={{
                                        width: 36,
                                        height: 36,
                                        background: completado ? estadoConfig.color : "var(--fondo-claro)",
                                        color: completado ? "var(--blanco)" : "var(--texto-gris-oscuro)",
                                        border: activo ? `3px solid ${estadoConfig.fondo}` : "1px solid var(--color-border)",
                                        zIndex: 1,
                                    }}
                                >
                                    {completado ? <i className="bi bi-check-lg"></i> : paso.numero}
                                </div>

                                <div className="pb-1">
                                    <p className="fw-semibold text-dark mb-1">{paso.titulo}</p>
                                    <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>{paso.texto}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* COMPLETAR RESERVA Y DISPUTA */}
                {estadoConfig.mostrarBotonCompletar && quienEs === "Comprador" ? (
                    puedeCompletarOReclamar ? (
                        <div>
                            <ModalCompletarReserva reserva={reserva} quienEs={quienEs} />
                            <ModalDisputas quienEs={quienEs} reserva={reserva} />
                        </div>
                    ) : (
                        <div
                            className="alert border-0 rounded-4 mt-4 mb-0 d-flex align-items-start gap-3"
                            style={{ background: "rgba(var(--azul-medio-rgb), 0.06)", color: "var(--color-text-primary)" }}
                        >
                            <i className="bi bi-lock" style={{ color: "var(--color-text-secondary)", fontSize: "1.2rem" }}></i>
                            <div>
                                <p className="mb-1 small text-muted">
                                    Estas opciones se habilitarán cuando llegue la hora de finalización del servicio.
                                </p>
                                <p className="mb-0 small fw-semibold" style={{ color: "var(--color-text-primary)" }}>
                                    Fecha fin del servicio: {fechaFin}
                                </p>
                            </div>
                        </div>
                    )
                ) : null}


                
                {/* TIEMPO RESTANTE PARA CONFIRMAR / CAMBIAR EL HORARIO (flexible) */}
                {mostrarModalesHorario && reserva?.confirmacionHorarioExpiraEn ? (
                    <CuentaRegresivaHorario
                        expiraEn={reserva.confirmacionHorarioExpiraEn}
                        ahoraServidor={ahoraServidor}
                        horaPropuesta={reserva?.horaSolicitada}
                    />
                ) : null}

                {/* CONFIRMAR / MODIFICAR HORARIO */}
                {puedeResponderHorario ? (
                    <>
                        <ModalConfirmarHorario quienEs={quienEs} reserva={reserva} />
                        <ModalCambiarHorario
                            reserva={reserva}
                            quienEs={quienEs}
                            usuario={usuario}
                            servicio={servicio}
                            ahoraServidor={ahoraServidor}
                        />
                    </>
                ) : mostrarModalesHorario ? (
                    <div
                        className="alert border-0 rounded-4 mt-4 mb-0 d-flex align-items-start gap-3"
                        style={{ background: "rgba(var(--naranja-cta-rgb), 0.10)", color: "var(--color-text-primary)" }}
                    >
                        <i className="bi bi-hourglass-split" style={{ color: "var(--naranja-cta)", fontSize: "1.2rem" }}></i>
                        <div>
                            <p className="fw-semibold mb-1">{textoEsperandoHorario}</p>
                            <p className="mb-0 small text-muted">
                                La otra parte debe confirmar o proponer un nuevo horario para continuar con la reserva.
                            </p>
                        </div>
                    </div>
                ) : null}


                {/* MODAL VALORACION */}
                {!soloLectura && estado === "completado" && !yaEnvioValoracion ? (
                    <ModalValoracion
                        idReserva={reserva._id}
                        idServicio={servicio.idServicio}
                        idComprador={reserva.idComprador}
                        idVendedor={reserva.idProveedor}
                        nombreUsuario={usuario.username}
                    />
                ) : null}
            </div>
        </div>
    );
}


export default EstadoReserva;